import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { 
  Target, 
  Eye, 
  Heart, 
  User, 
  MapPin, 
  Globe,
  Award,
  Users,
  Calendar,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { companyInfo, team, stats } from '../mock';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const milestones = [
    {
      year: "2020",
      title: "Création de SET-G",
      description: "Fondation de l'entreprise par Georges KASSI avec une vision claire : excellence en ingénierie des télécommunications."
    },
    {
      year: "2021", 
      title: "Premiers Grands Projets",
      description: "Démarrage des activités avec les premiers contrats d'audit QoS et projets d'infrastructure."
    },
    {
      year: "2022",
      title: "Reconnaissance Marché",
      description: "Expansion du portefeuille clients et reconnaissance comme expert en audit QoS par les régulateurs."
    },
    {
      year: "2023",
      title: "Croissance Soutenue",
      description: "Consolidation de la position sur le marché avec des projets d'envergure pour l'ARTCI et d'autres clients majeurs."
    },
    {
      year: "2024-25",
      title: "Leader Technologique",
      description: "Position de leader en audit et benchmarking en Côte d'Ivoire avec plus de 255 localités auditées."
    }
  ];

  const achievements = [
    {
      icon: Award,
      title: "13+ Projets Réalisés",
      description: "Projets d'envergure avec des clients prestigieux"
    },
    {
      icon: Users,
      title: "6+ Clients Satisfaits",
      description: "Partenariats durables avec les acteurs majeurs"
    },
    {
      icon: TrendingUp,
      title: `${new Date().getFullYear() - 2020}+ Années d'Excellence`,
      description: "Expertise reconnue et innovation continue"
    }
  ];

  const siteUrl = "https://www.set-g.com";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${siteUrl}/a-propos#aboutpage`,
    "name": "À Propos de SET-G - Expert Télécoms Côte d'Ivoire",
    "url": `${siteUrl}/a-propos`,
    "mainEntity": {
      "@type": "Organization",
      "name": companyInfo.name,
      "alternateName": companyInfo.fullName,
      "description": companyInfo.description,
      "foundingDate": "2020",
      "founder": {
        "@type": "Person",
        "name": team.leader.name,
        "jobTitle": team.leader.role,
        "description": team.leader.experience,
        "alumniOf": [
          {
            "@type": "EducationalOrganization",
            "name": "University of Cumbria, United Kingdom"
          },
          {
            "@type": "EducationalOrganization", 
            "name": "INP-HB Yamoussoukro"
          }
        ]
      },
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "minValue": 10,
        "maxValue": 50
      },
      "location": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Angré Face Soleil 3, immeuble restaurant Wallace",
          "addressLocality": "Abidjan Cocody",
          "addressCountry": "CI"
        }
      },
      "knowsAbout": ["Audit QoS", "Ingénierie Radio", "Télécommunications", "Infrastructure IT"]
    }
  };

  return (
    <>
      <SEOHead
        title="À Propos | Georges KASSI, 25+ ans d'expertise Télécoms"
        description="SET-G fondé en 2020 par Georges KASSI (MBA, Ingénieur). +25 ans d'expérience internationale en télécoms (Huawei, Siemens, Asiacell). Leader audit QoS en Côte d'Ivoire. Valeurs: Innovation, Performance, Intégrité."
        keywords="à propos SET-G, Georges KASSI fondateur, histoire entreprise télécoms Côte d'Ivoire, équipe expert audit QoS, mission vision valeurs, MBA University Cumbria, INP-HB Yamoussoukro"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                À Propos de SET-G
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Notre Histoire & Vision
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Depuis {new Date().getFullYear() - parseInt(companyInfo.foundingYear)} ans, nous accompagnons les entreprises et organismes publics 
                dans leur transformation digitale avec passion et expertise.
              </p>
            </div>
          </div>
        </section>

        {/* Mission, Vision, Valeurs */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 mb-20">
              {/* Vision */}
              <Card className="text-center hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Eye className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-slate-900">
                    Notre Vision
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed text-lg">
                    {companyInfo.vision} et être une entreprise avant-gardiste 
                    de solutions vertes et durables pour l'Afrique.
                  </p>
                </CardContent>
              </Card>

              {/* Valeurs */}
              <Card className="text-center hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-green-600" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-slate-900">
                    Nos Valeurs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {companyInfo.values.map((value, index) => (
                      <Badge 
                        key={index} 
                        variant="secondary" 
                        className="bg-slate-100 text-slate-700 hover:bg-slate-200 text-base px-3 py-1"
                      >
                        {value}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-8 mb-20">
              {achievements.map((achievement, index) => {
                const Icon = achievement.icon;
                return (
                  <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="pt-8">
                      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-orange-600" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">
                        {achievement.title}
                      </h3>
                      <p className="text-slate-600">
                        {achievement.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Notre Parcours
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                {new Date().getFullYear() - parseInt(companyInfo.foundingYear)} années d'innovation et de croissance au service de l'excellence
              </p>
            </div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="lg:w-1/4">
                    <div className="text-center lg:text-right">
                      <div className="inline-flex items-center justify-center w-24 h-24 bg-orange-600 text-white rounded-full text-lg font-bold mb-4">
                        {milestone.year}
                      </div>
                    </div>
                  </div>
                  
                  <div className="lg:w-3/4">
                    <Card className="hover:shadow-lg transition-shadow duration-300">
                      <CardContent className="pt-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-3 h-3 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                              {milestone.title}
                            </h3>
                            <p className="text-slate-600 leading-relaxed">
                              {milestone.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Leadership */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Leadership & Expertise
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Une équipe dirigée par des experts passionnés et expérimentés
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Card className="hover:shadow-xl transition-shadow duration-300">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-orange-200">
                        <img 
                          src={team.leader.photo}
                          alt={`${team.leader.name} - ${team.leader.role} de SET-G, expert télécommunications Côte d'Ivoire`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <CardTitle className="text-2xl font-bold text-slate-900">
                          {team.leader.name}
                        </CardTitle>
                        <CardDescription className="text-orange-600 font-medium text-lg">
                          {team.leader.role}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Qualifications :</h4>
                        <div className="space-y-2">
                          {team.leader.qualifications.map((qual, index) => (
                            <div key={index} className="flex items-center space-x-3">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <span className="text-slate-600">{qual}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <p className="text-slate-600 italic border-t pt-4">
                        {team.leader.experience}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="bg-slate-900 text-white">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold mb-4">Notre Équipe</h3>
                    <p className="text-slate-300 leading-relaxed">
                      {team.description}
                    </p>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Globe className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                      <h4 className="font-semibold text-slate-900">Expertise Internationale</h4>
                      <p className="text-sm text-slate-600 mt-1">Projets en Afrique de l'Ouest</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <h4 className="font-semibold text-slate-900">Expertise Reconnue</h4>
                      <p className="text-sm text-slate-600 mt-1">Partenaire ARTCI, Expert QoS</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Rejoignez Notre Histoire de Succès
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Découvrez comment notre expertise peut transformer vos projets en réussites durables.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-orange-600 hover:bg-orange-700">
                <Link to="/contact">
                  Travaillons Ensemble
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-slate-900">
                <Link to="/services">
                  Nos Services
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;